import { useState } from 'react';
import { Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { ArrowRight, Check, ChevronDown, Scale } from 'lucide-react';
import { PADDLES, SELECTOR, METRICS, TECH_FAQ, DATA_REFERENCE, TECH_IMG } from '../../data/technicals';
import { MetricBars } from './MetricBars';

const btnBase = 'inline-flex items-center justify-center gap-2 rounded-full font-bold uppercase tracking-[0.15em] text-xs sm:text-sm px-6 py-3 transition-all duration-200 hover:scale-[1.03] active:scale-[0.97]';

// Equal CTAs for both paddles + compare
export const PaddleCtas = ({ compare = true, className = '' }) => (
  <div className={`flex flex-wrap items-center gap-3 ${className}`} data-testid="paddle-ctas">
    <Link to="/product/hexcore" className={`${btnBase} text-white bg-[#E02128] shadow-[0_12px_30px_rgba(224,33,40,0.3)]`} data-testid="cta-shop-hexcore">
      Shop HexCore <ArrowRight className="w-4 h-4" />
    </Link>
    <Link to="/product/profoam" className={`${btnBase} text-white bg-[#1E6FD9] shadow-[0_12px_30px_rgba(30,111,217,0.3)]`} data-testid="cta-shop-profoam">
      Shop ProFoam <ArrowRight className="w-4 h-4" />
    </Link>
    {compare && (
      <Link to="/compare" className={`${btnBase} text-white border border-white/25 hover:border-white/60 hover:bg-white/5`} data-testid="cta-compare">
        <Scale className="w-4 h-4" /> Compare paddles
      </Link>
    )}
  </div>
);

// Preference pathways → recommendation
export const PaddleSelector = () => {
  const [sel, setSel] = useState(null);
  const row = sel != null ? SELECTOR[sel] : null;
  const picks = row ? (row.pick === 'both' ? ['hexcore', 'profoam'] : [row.pick]) : [];
  return (
    <div className="grid lg:grid-cols-12 gap-6 lg:gap-10" data-testid="paddle-selector">
      <div className="lg:col-span-6 space-y-2">
        {SELECTOR.map((s, i) => {
          const active = sel === i;
          return (
            <button
              key={s.pref}
              onClick={() => setSel(i)}
              className={`w-full text-left flex items-center justify-between gap-4 px-4 sm:px-5 py-3.5 rounded-xl border transition-all duration-200 ${
                active ? 'border-white/40 bg-white/[0.07]' : 'border-white/10 bg-white/[0.02] hover:bg-white/[0.05] hover:border-white/20'
              }`}
              data-testid={`selector-option-${i}`}
            >
              <span className="text-sm sm:text-base text-zinc-100">{s.pref}</span>
              <span className={`w-5 h-5 rounded-full border flex items-center justify-center shrink-0 ${active ? 'border-white bg-white text-black' : 'border-white/30'}`}>
                {active && <Check className="w-3 h-3" />}
              </span>
            </button>
          );
        })}
      </div>
      <div className="lg:col-span-6">
        <div className="relative min-h-[260px] rounded-2xl border border-white/10 bg-white/[0.03] p-6 sm:p-8 overflow-hidden" data-testid="selector-result">
          <AnimatePresence mode="wait">
            {!row ? (
              <motion.div key="empty" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="h-full flex flex-col justify-center">
                <p className="font-mono text-[10px] uppercase tracking-[0.3em] text-zinc-500">Paddle selector</p>
                <h3 className="mt-3 font-heading font-bold text-2xl text-white">Which DYSSC build suits your preference?</h3>
                <p className="mt-3 text-zinc-400 text-sm">Pick what matters most to you. Both paddles have clear pathways to purchase.</p>
              </motion.div>
            ) : (
              <motion.div key={sel} initial={{ opacity: 0, y: 14 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -10 }} transition={{ duration: 0.22 }}>
                <p className="font-mono text-[10px] uppercase tracking-[0.3em] text-zinc-500">Starting point</p>
                <div className="mt-3 flex flex-wrap gap-3">
                  {picks.map((k) => (
                    <span key={k} className="font-display text-3xl sm:text-4xl uppercase tracking-tight" style={{ color: PADDLES[k].accent }}>
                      {PADDLES[k].name}
                    </span>
                  ))}
                </div>
                <p className="mt-4 text-zinc-200 text-sm sm:text-base leading-relaxed">{row.reason}</p>
                <div className="mt-6 flex flex-wrap gap-3">
                  {picks.map((k) => (
                    <Link key={k} to={`/product/${k}`} className={`${btnBase} text-white`} style={{ backgroundColor: PADDLES[k].accent }} data-testid={`selector-cta-${k}`}>
                      Explore {PADDLES[k].name} <ArrowRight className="w-4 h-4" />
                    </Link>
                  ))}
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </div>
    </div>
  );
};

// Side-by-side product-data table with inline bars
export const ComparisonTable = ({ showImages = true }) => (
  <div className="rounded-2xl border border-white/10 overflow-hidden bg-[#0F0F11]" data-testid="comparison-table">
    <div className="grid grid-cols-[1.1fr_1fr_1fr] sm:grid-cols-[1.3fr_1fr_1fr] border-b border-white/10 bg-white/[0.03]">
      <div className="p-4 sm:p-6 flex items-end"><span className="font-mono text-[10px] uppercase tracking-[0.3em] text-zinc-500">Product data</span></div>
      {['hexcore', 'profoam'].map((k) => (
        <div key={k} className="p-4 sm:p-6 text-center">
          {showImages && (
            <img src={k === 'hexcore' ? TECH_IMG.paddleHex : TECH_IMG.paddlePro} alt={PADDLES[k].fullName} className="mx-auto h-28 sm:h-40 w-auto object-contain drop-shadow-[0_18px_30px_rgba(0,0,0,0.6)]" />
          )}
          <p className="mt-2 font-display text-xl sm:text-2xl uppercase tracking-tight" style={{ color: PADDLES[k].accent }}>{PADDLES[k].name}</p>
          <p className="text-[11px] sm:text-xs text-zinc-400">{PADDLES[k].identity}</p>
        </div>
      ))}
    </div>
    {METRICS.map((m, i) => (
      <div key={m.key} className={`grid grid-cols-[1.1fr_1fr_1fr] sm:grid-cols-[1.3fr_1fr_1fr] items-start border-b border-white/[0.06] ${i % 2 ? 'bg-white/[0.015]' : ''}`} data-testid={`compare-row-${m.key}`}>
        <div className="p-4 sm:p-5">
          <p className="text-sm sm:text-base text-zinc-100 font-medium">{m.label}</p>
          {m.condition && <p className="text-[11px] text-zinc-500 mt-0.5">{m.condition}</p>}
        </div>
        {['hexcore', 'profoam'].map((k) => (
          <div key={k} className="p-4 sm:p-5 text-center">
            {m.type === 'text' ? (
              <p className="text-sm sm:text-base text-zinc-100">{m[k]}</p>
            ) : (
              <>
                <p className="font-mono text-base sm:text-xl text-white">{m[k]} <span className="text-[10px] sm:text-xs text-zinc-500">{m.unit}</span></p>
                <div className="mt-2 mx-auto h-1.5 w-full max-w-[140px] rounded-full bg-white/[0.06] overflow-hidden">
                  <div className="h-full rounded-full" style={{ width: `${(m[k] / m.max) * 100}%`, background: PADDLES[k].accent }} />
                </div>
                {m.leads === k && <p className="mt-1.5 font-mono text-[9px] uppercase tracking-[0.2em]" style={{ color: PADDLES[k].accent }}>{m.leadNote}</p>}
              </>
            )}
          </div>
        ))}
      </div>
    ))}
    <div className="p-4 sm:p-5 text-[11px] text-zinc-500">
      Swing weight = effort to swing. Twist weight = steadiness on off-centre hits. Lower face bend = firmer feel. Details —{' '}
      <a href="#data-reference" className="underline underline-offset-2 hover:text-zinc-300" data-testid="data-reference-link">Data reference</a>.
    </div>
  </div>
);

export const TechFaq = ({ items = TECH_FAQ, dark = true }) => {
  const [open, setOpen] = useState(0);
  return (
    <div className={`divide-y ${dark ? 'divide-white/10' : 'divide-neutral-200'}`} data-testid="tech-faq">
      {items.map((f, i) => (
        <div key={f.q}>
          <button onClick={() => setOpen(open === i ? -1 : i)} className="w-full flex items-center justify-between gap-4 py-4 text-left" data-testid={`faq-q-${i}`}>
            <span className={`font-heading font-semibold text-base sm:text-lg ${dark ? 'text-white' : 'text-[#0A0A0A]'}`}>{f.q}</span>
            <ChevronDown className={`w-5 h-5 shrink-0 transition-transform duration-300 ${open === i ? 'rotate-180' : ''} ${dark ? 'text-zinc-400' : 'text-neutral-500'}`} />
          </button>
          <AnimatePresence initial={false}>
            {open === i && (
              <motion.div initial={{ height: 0, opacity: 0 }} animate={{ height: 'auto', opacity: 1 }} exit={{ height: 0, opacity: 0 }} transition={{ duration: 0.3 }} className="overflow-hidden">
                <p className={`pb-5 text-sm sm:text-base leading-relaxed ${dark ? 'text-zinc-400' : 'text-neutral-600'}`}>{f.a}</p>
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      ))}
    </div>
  );
};

export const DataReference = () => {
  const [open, setOpen] = useState(false);
  return (
    <div id="data-reference" className="rounded-xl border border-white/10 bg-white/[0.02]" data-testid="data-reference">
      <button onClick={() => setOpen(!open)} className="w-full flex items-center justify-between gap-4 px-5 py-4 text-left" data-testid="data-reference-toggle">
        <span className="font-mono text-[10px] uppercase tracking-[0.3em] text-zinc-400">Data reference</span>
        <ChevronDown className={`w-4 h-4 text-zinc-500 transition-transform duration-300 ${open ? 'rotate-180' : ''}`} />
      </button>
      <AnimatePresence initial={false}>
        {open && (
          <motion.div initial={{ height: 0, opacity: 0 }} animate={{ height: 'auto', opacity: 1 }} exit={{ height: 0, opacity: 0 }} className="overflow-hidden">
            <div className="px-5 pb-5 text-xs sm:text-sm text-zinc-400 leading-relaxed space-y-2">
              <p>{DATA_REFERENCE}</p>
              <p>Swing weight: effort to swing, measured about a point 5 cm from the end of the handle. Face bend: how far the face dips per kg on soft shots (1–3 kg). Impact sound: the tone of the pop, not its loudness.</p>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

// Compact metric grid used on the Compare page sidebar / product pages
export const KeyMetricsStrip = ({ dark = true }) => (
  <div className="grid sm:grid-cols-2 gap-6" data-testid="key-metrics-strip">
    {METRICS.filter((m) => ['weight', 'swing', 'twist', 'bending'].includes(m.key)).map((m) => (
      <div key={m.key} className={`rounded-xl border p-4 sm:p-5 ${dark ? 'border-white/10 bg-white/[0.02]' : 'border-neutral-200 bg-white'}`}>
        <MetricBars metric={m} dark={dark} />
      </div>
    ))}
  </div>
);
